/**
 * @module components/Cart
 * @description Brief §4.6 donation cart: editable cart lines (amount, note,
 * remove), sticky cart bar, slide-out cart panel with Secure Checkout,
 * Continue Browsing, tax-deductible note and trust points; plus the toast.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate, useRoute } from "../app/router";
import type { Cause, CartLine } from "../domain/types";
import { formatUsd, plural } from "../lib/format";
import { Icon } from "./Icons";
import { TrustList } from "./Trust";
import "./Cart.css";

/** One editable cart line. `editable=false` renders a read-only summary row. */
export function CartLineRow({ line, editable }: { line: CartLine; editable: boolean }): JSX.Element | null {
    const { causeById, dispatchCart } = useGiving();
    const [noteOpen, setNoteOpen] = useState<boolean>(line.note !== "");
    // BUG FIX: edit the amount as a local draft string.
    //   What caused this: the input dispatched every keystroke; clearing the field
    //   sent Number("") = 0, which normalizeAmount() snapped to $1 mid-edit.
    //   Why we chose it: a draft lets the donor clear and retype; only whole
    //   amounts ≥ 1 reach the cart, and an invalid draft reverts on blur.
    //   Side-effects: the cart total updates per valid keystroke, not per empty one.
    const [draft, setDraft] = useState<string>(String(line.amountUsd));
    useEffect(() => setDraft(String(line.amountUsd)), [line.amountUsd]);
    const cause: Cause | undefined = causeById(line.causeId);
    if (cause === undefined) return null;

    return (
        <li className="cart-line">
            <img src={cause.image.src} alt="" className="cart-line__img" />
            <div className="cart-line__main">
                <p className="cart-line__partner">{cause.partner}</p>
                <p className="cart-line__title">{cause.title}</p>
                {editable ? (
                    <>
                        <div className="cart-line__controls">
                            <label className="cart-line__amount">
                                <span className="visually-hidden">Amount for {cause.title}</span>
                                <span aria-hidden="true">$</span>
                                <input type="number" inputMode="numeric" min={1} step={1} value={draft}
                                    onChange={(e) => {
                                        setDraft(e.target.value);
                                        const n: number = Number.parseInt(e.target.value, 10);
                                        if (Number.isFinite(n) && n >= 1) dispatchCart({ type: "setAmount", causeId: cause.id, amountUsd: n });
                                    }}
                                    onBlur={() => setDraft(String(line.amountUsd))} />
                            </label>
                            <button type="button" className="cart-line__link" onClick={() => setNoteOpen((o: boolean) => !o)} aria-expanded={noteOpen}>
                                {noteOpen ? "Hide note" : "Add a note"}
                            </button>
                            <button type="button" className="cart-line__link cart-line__remove" onClick={() => dispatchCart({ type: "remove", causeId: cause.id })}>
                                Remove
                            </button>
                        </div>
                        {noteOpen && (
                            <label className="cart-line__note">
                                <span className="visually-hidden">Note for {cause.title}</span>
                                <textarea className="textarea" maxLength={280} value={line.note} placeholder="Optional: in honor of…, or a message to AFJ"
                                    onChange={(e) => dispatchCart({ type: "setNote", causeId: cause.id, note: e.target.value })} />
                            </label>
                        )}
                    </>
                ) : (
                    line.note !== "" && <p className="cart-line__note-text">“{line.note}”</p>
                )}
            </div>
            {!editable && <p className="cart-line__fixed">{formatUsd(line.amountUsd)}</p>}
        </li>
    );
}

/** Slide-out cart panel. */
export function CartDrawer(): JSX.Element {
    const { cartOpen, setCartOpen, lines, totalUsd } = useGiving();
    const panel = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const el: HTMLDialogElement | null = panel.current;
        if (el === null) return;
        if (cartOpen && !el.open) el.showModal();
        if (!cartOpen && el.open) el.close();
    }, [cartOpen]);

    const checkout = (): void => {
        setCartOpen(false);
        navigate("checkout");
    };

    return (
        <dialog ref={panel} className="cart-drawer" onClose={() => setCartOpen(false)} onClick={(e) => { if (e.target === panel.current) setCartOpen(false); }} aria-labelledby="cart-title">
            <div className="cart-drawer__inner">
                <header className="cart-drawer__head">
                    <h2 id="cart-title">Your donation cart</h2>
                    <button type="button" className="icon-btn" onClick={() => setCartOpen(false)} aria-label="Close cart"><Icon name="close" /></button>
                </header>

                {lines.length === 0 ? (
                    <div className="cart-drawer__empty">
                        <Icon name="cart" size={40} />
                        <p>Your cart is empty. Add a cause or two, then give to all of them at once.</p>
                        <button type="button" className="btn btn--primary" onClick={() => setCartOpen(false)}>Browse causes</button>
                    </div>
                ) : (
                    <>
                        <ul className="cart-drawer__lines">
                            {lines.map((line: CartLine) => <CartLineRow key={line.causeId} line={line} editable />)}
                        </ul>
                        <footer className="cart-drawer__foot">
                            <div className="cart-drawer__total">
                                <span>{plural(lines.length, "cause")}</span>
                                <strong>{formatUsd(totalUsd)}</strong>
                            </div>
                            <p className="cart-drawer__tax">Your gift is a tax-deductible gift to The American Friends of Jamaica, designated to the causes above.</p>
                            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={checkout}><Icon name="lock" /> Secure Checkout</button>
                            <button type="button" className="btn btn--ghost btn--block" onClick={() => setCartOpen(false)}>Continue Browsing</button>
                            <TrustList compact />
                        </footer>
                    </>
                )}
            </div>
        </dialog>
    );
}

/** Sticky bottom bar: "Your donation cart · 2 causes · $150 · View" (mockup). */
export function CartBar(): JSX.Element | null {
    const { lines, totalUsd, setCartOpen } = useGiving();
    const route = useRoute();
    if (lines.length === 0 || route !== "browse") return null;
    return (
        <div className="cart-bar" role="region" aria-label="Donation cart summary">
            <div className="container cart-bar__inner">
                <span className="cart-bar__icon"><Icon name="cart" /></span>
                <span className="cart-bar__text"><strong>Your donation cart</strong> · {plural(lines.length, "cause")} · <strong>{formatUsd(totalUsd)}</strong></span>
                <button type="button" className="btn btn--gold" onClick={() => setCartOpen(true)}>View</button>
            </div>
        </div>
    );
}

/** Transient confirmation message, announced to screen readers. */
export function Toast(): JSX.Element {
    const { toast } = useGiving();
    return (
        <div className="toast-region" aria-live="polite" role="status">
            {toast !== null && <div className="toast" key={toast}><Icon name="check" size={18} /> {toast}</div>}
        </div>
    );
}
